/**
 * Home View
 * Pure presentational component - NO business logic
 */

import {
  Box,
  Button,
  Container,
  Text,
  VStack,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ColorModeButton } from "../../../components/ui/color-mode";
import { LanguageSwitcher } from "../../../components/LanguageSwitcher";
import { AssetImage } from "../../../components/AssetImage";
import type { HomeViewModel } from "../viewModel/useHomeViewModel";
import type { Contact } from "../../../api";

// Phone icon for the call button
const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
  </svg>
);

// Logout icon
const LogoutIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" />
  </svg>
);

interface HomeViewProps {
  viewModel: HomeViewModel;
}

export function HomeView({ viewModel }: HomeViewProps) {
  const { t, i18n } = useTranslation();
  const {
    config,
    contacts,
    isLoading,
    error,
    agentName,

    isMicrophoneGranted,
    handleLogout,
    handleCall,
    handleRequestMicrophone,
  } = viewModel;

  // Get current language code (e.g., "en", "id", "hi")
  const currentLanguage = i18n.language?.split("-")[0] || "en";

  // Loading state
  if (isLoading) {
    return (
      <Box
        minH="100vh"
        bg="bg"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <VStack gap="4">
          <Spinner size="xl" color="accent" />
          <Text color="fg.muted">{t("common.loadingContacts")}</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box bg="bg" color="fg" minH="100vh">
      <Container maxW="lg" py="8">
        <VStack gap="8">
          {/* Header */}
          <Box
            w="full"
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            p="6"
            bg={{ base: "white", _dark: "rgba(15, 23, 42, 0.7)" }}
            borderRadius="2xl"
            boxShadow={{
              base: "0 4px 20px rgba(0, 0, 0, 0.06)",
              _dark:
                "0 4px 24px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            border="1px solid"
            borderColor={{ base: "gray.100", _dark: "rgba(99,102,241,0.2)" }}
            backdropFilter={{ _dark: "blur(16px)" }}
          >
            <VStack align="start" gap={3}>
              {/* Logo integration */}
              {config.logo && (
                <Box
                  w={{ base: "16", md: "20" }}
                  h={{ base: "16", md: "20" }}
                  borderRadius="md"
                  overflow="hidden"
                  flexShrink={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-start"
                >
                  <AssetImage assetId={config.logo} alt="App Logo" />
                </Box>
              )}
              <Text
                color={{ base: "gray.500", _dark: "whiteAlpha.600" }}
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="medium"
              >
                {t("common.welcome")},{" "}
                <Text
                  as="span"
                  color={{ base: "gray.800", _dark: "whiteAlpha.900" }}
                  fontWeight="bold"
                  fontSize={{ base: "lg", md: "xl" }}
                >
                  {agentName}
                </Text>
              </Text>
            </VStack>
            <Box display="flex" alignItems="center" gap="3">
              {/* Language Switcher */}
              <LanguageSwitcher />

              <ColorModeButton size="md" />
            </Box>
          </Box>

          {/* Microphone permission request */}
          {!isMicrophoneGranted && (
            <Box
              w="full"
              p="4"
              bg="accent.muted"
              borderRadius="lg"
              border="1px solid"
              borderColor="accent"
            >
              <VStack gap="2">
                <Text color="fg" fontSize="sm" textAlign="center">
                  {t("common.microphoneAccessRequired")}
                </Text>
                <Button
                  size="sm"
                  colorPalette="blue"
                  onClick={handleRequestMicrophone}
                >
                  {t("common.grantAccess")}
                </Button>
              </VStack>
            </Box>
          )}

          {/* Contact List */}
          <VStack w="full" gap="3">
            {(!Array.isArray(contacts) || contacts.length === 0) && !error ? (
              <Box
                w="full"
                p="8"
                bg="bg.subtle"
                borderRadius="xl"
                textAlign="center"
              >
                <Text color="fg.muted">{t("common.noContacts")}</Text>
              </Box>
            ) : (
              Array.isArray(contacts) &&
              contacts.map((contact) => (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  currentLanguage={currentLanguage}
                  onCall={handleCall}
                  // disabled={!isSipConnected || !isMicrophoneGranted}
                />
              ))
            )}
          </VStack>

          {/* Logout Button */}
          <Button
            variant="solid"
            size="md"
            color="white"
            bg="red.500"
            onClick={handleLogout}
            mt="8"
            px="8"
            py="2"
            borderRadius="full"
            fontWeight="bold"
            _hover={{
              bg: "red.600",
              transform: "translateY(-1px)",
              boxShadow: "0 4px 12px rgba(229, 62, 62, 0.4)",
            }}
            _active={{
              transform: "translateY(0)",
            }}
            _dark={{
              bg: "red.600",
              _hover: {
                bg: "red.700",
                boxShadow: "0 4px 12px rgba(229, 62, 62, 0.6)",
              },
            }}
            transition="all 0.2s ease"
          >
            <LogoutIcon />
            <Text ml="2">{t("header.logout")}</Text>
          </Button>
        </VStack>
      </Container>
    </Box>
  );
}

// Subcomponent for contact card (purely presentational)
interface ContactCardProps {
  contact: Contact;
  currentLanguage: string;
  onCall: (contact: Contact) => void;
  disabled?: boolean;
}

function ContactCard({
  contact,
  currentLanguage,
  onCall,
  disabled,
}: ContactCardProps) {
  const { t } = useTranslation();

  // Get localized contact name based on current language
  // Falls back to English if the current language is not available
  const getLocalizedName = (): string => {
    return (
      contact.contactName[currentLanguage] ||
      contact.contactName["en"] ||
      Object.values(contact.contactName)[0] ||
      "Unknown"
    );
  };

  const localizedName = getLocalizedName();

  const iconSrc = contact.contactIcon || null;

  // Helper to create gradient with hover/active states from a single color
  const createButtonGradient = (baseColor: string) => {
    // For now, use solid colors with opacity variations
    // You can enhance this later with actual gradient generation
    return {
      bg: baseColor,
      hoverBg: baseColor,
      activeBg: baseColor,
    };
  };

  const buttonGradient = createButtonGradient(contact.actionButtonColorTheme);

  return (
    <Box
      w="full"
      p="5"
      bg={contact.contactCardBgColor}
      borderRadius="2xl"
      border="1px solid"
      borderColor="whiteAlpha.400"
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      opacity={disabled ? 0.6 : 1}
      boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
      _hover={{
        transform: disabled ? "none" : "translateY(-2px)",
        boxShadow: disabled
          ? "0 4px 20px rgba(0, 0, 0, 0.08)"
          : "0 8px 30px rgba(0, 0, 0, 0.12)",
        borderColor: disabled ? "whiteAlpha.400" : "whiteAlpha.600",
      }}
      transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      cursor={disabled ? "not-allowed" : "pointer"}
    >
      <Box display="flex" alignItems="center" gap="4">
        {/* Contact Icon with ring */}
        <Box
          w="14"
          h="14"
          borderRadius="full"
          bg={iconSrc ? "transparent" : contact.contactIconColorTheme}
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          flexShrink={0}
          boxShadow={iconSrc ? "none" : "0 4px 12px rgba(0, 0, 0, 0.15)"}
        >
          {iconSrc ? (
            <AssetImage assetId={iconSrc} alt={localizedName} />
          ) : (
            <Text fontWeight="bold" fontSize="xl" color="white">
              {localizedName.charAt(0).toUpperCase()}
            </Text>
          )}
        </Box>
        <VStack align="start" gap="1">
          <Text
            fontWeight="bold"
            fontSize="lg"
            color={contact.contactNameColorTheme}
            letterSpacing="-0.01em"
          >
            {localizedName}
          </Text>
        </VStack>
      </Box>
      <Button
        size="md"
        borderRadius="full"
        onClick={() => onCall(contact)}
        px="6"
        py="5"
        disabled={disabled}
        bg={buttonGradient.bg}
        color={contact.actionButtonTextColorTheme}
        fontWeight="bold"
        fontSize="md"
        boxShadow={`0 4px 20px ${contact.actionButtonColorTheme}50, 0 0 40px ${contact.actionButtonColorTheme}30`}
        _hover={{
          bg: buttonGradient.hoverBg,
          filter: "brightness(1.1)",
          boxShadow: `0 6px 28px ${contact.actionButtonColorTheme}60, 0 0 50px ${contact.actionButtonColorTheme}40`,
          transform: "scale(1.05)",
        }}
        _active={{
          transform: "scale(0.98)",
          bg: buttonGradient.activeBg,
          filter: "brightness(0.9)",
        }}
        transition="all 0.2s ease"
      >
        <PhoneIcon />
        <Text ml="2">{contact.actionButtonLabel || t("dialer.call")}</Text>
      </Button>
    </Box>
  );
}
